import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'
import type { VisitorWidget, WeeklyAvailability } from '@/types/visitor'

// ---------------------------------------------------------------------------
// EP-133 T133-019 — ⚠️ ASSUMED BACKEND CONTRACT, NOT YET IMPLEMENTED.
//
// At the time this hook layer was written, only ONE of the six write actions
// below is real: `rotate_public_key` (T133-015, uncommitted but present in
// api/app/controllers/api/visitor_widgets_controller.rb). Index/create/update/
// toggle/update_availability have no controller or route yet — VisitorWidgetPolicy
// (T133-014, committed) already defines exactly these six action names
// (`show?/create?/update?/destroy?/toggle?/update_availability?/rotate_public_key?`),
// so this hook layer targets that vocabulary rather than inventing a different
// one. Endpoint shapes below are modeled directly on the ParkingZone precedent
// (api/app/controllers/api/parking_zones_controller.rb — the nearest sibling:
// a per-property public-key-bearing config resource with its own rotate
// action). Do not treat this comment as authoritative once the real backend
// lands — reconcile against the actual controller/routes at that point.
// ---------------------------------------------------------------------------

export const visitorWidgetKeys = {
  all: ['visitor_widgets'] as const,
  lists: () => [...visitorWidgetKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...visitorWidgetKeys.lists(), filters] as const,
  details: () => [...visitorWidgetKeys.all, 'detail'] as const,
  detail: (id: number) => [...visitorWidgetKeys.details(), id] as const,
}

// GET /api/properties/:propertyId/visitor_widgets — index (mirrors
// GET /api/properties/:propertyId/parking_zones). A property has at most one
// widget in practice (§3 of the plan: "per-property, not per-account"), but
// the endpoint returns a list, same shape as ParkingZone, rather than a
// singleton 404-until-created resource.
export function useVisitorWidgets(propertyId: number) {
  return useQuery({
    queryKey: visitorWidgetKeys.list({ propertyId }),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<VisitorWidget[]>>(
        `/api/properties/${propertyId}/visitor_widgets`
      )
      return data.data
    },
    enabled: !!propertyId,
  })
}

// POST /api/properties/:propertyId/visitor_widgets — create. Called explicitly
// by the settings page's "create widget" empty-state action (never auto-fired
// on mount) so provisioning stays a visible staff decision, not a silent
// side effect of viewing the page.
export interface CreateVisitorWidgetInput {
  property_id: number
  slot_minutes?: number
  lead_time_minutes?: number
  max_per_slot?: number
  booking_window_days?: number
}

export function useCreateVisitorWidget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ property_id, ...rest }: CreateVisitorWidgetInput) => {
      const { data } = await api.post<ApiResponse<VisitorWidget>>(
        `/api/properties/${property_id}/visitor_widgets`,
        { visitor_widget: rest }
      )
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, visitorWidgetKeys.lists())
    },
  })
}

// PATCH /api/visitor_widgets/:id — slot settings only (availability has its
// own dedicated action below, matching VisitorWidgetPolicy#update_availability?
// being distinct from #update?).
export interface UpdateVisitorWidgetInput {
  id: number
  slot_minutes: number
  lead_time_minutes: number
  max_per_slot: number
  booking_window_days: number
}

export function useUpdateVisitorWidget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateVisitorWidgetInput) => {
      const { data } = await api.patch<ApiResponse<VisitorWidget>>(`/api/visitor_widgets/${id}`, {
        visitor_widget: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, visitorWidgetKeys.lists())
      invalidateDetailQueries(queryClient, visitorWidgetKeys.detail(data.id))
    },
  })
}

// PATCH /api/visitor_widgets/:id/update_availability — the GBP-style weekly
// windows only. Kept as its own request (not folded into `update`) because
// S-2 requires availability edits to NEVER touch existing bookings; isolating
// the write path makes that guarantee easier to keep true server-side and
// easier to verify here (a save that only narrows hours issues exactly one
// request, to this endpoint, and nothing that could plausibly touch a
// visitor_visits row).
export interface UpdateVisitorWidgetAvailabilityInput {
  id: number
  availability: WeeklyAvailability
}

export function useUpdateVisitorWidgetAvailability() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, availability }: UpdateVisitorWidgetAvailabilityInput) => {
      const { data } = await api.patch<ApiResponse<VisitorWidget>>(
        `/api/visitor_widgets/${id}/update_availability`,
        { visitor_widget: { availability } }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, visitorWidgetKeys.lists())
      invalidateDetailQueries(queryClient, visitorWidgetKeys.detail(data.id))
    },
  })
}

// POST /api/visitor_widgets/:id/toggle — enable/disable, fired immediately on
// click (not gated behind the form's Save/Cancel) since it takes the public
// widget on/off the moment staff decides, independent of any in-progress
// availability/slot-setting edits they haven't saved yet.
export interface ToggleVisitorWidgetInput {
  id: number
  enabled: boolean
}

export function useToggleVisitorWidget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, enabled }: ToggleVisitorWidgetInput) => {
      const { data } = await api.post<ApiResponse<VisitorWidget>>(
        `/api/visitor_widgets/${id}/toggle`,
        { visitor_widget: { enabled } }
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, visitorWidgetKeys.lists())
      invalidateDetailQueries(queryClient, visitorWidgetKeys.detail(data.id))
    },
  })
}

// POST /api/visitor_widgets/:id/rotate_public_key — the ONE real endpoint of
// this file (T133-015). Issues a fresh key immediately, invalidating the old
// one — the caller MUST confirm first when a key already exists (see
// VisitorWidgetSettingsPage's ConfirmDialog; rotating invalidates the LIVE
// embedded iframe on the operator's own website).
export interface RotateVisitorWidgetKeyResult {
  id: number
  public_key: string
}

export function useRotateVisitorWidgetKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ApiResponse<RotateVisitorWidgetKeyResult>>(
        `/api/visitor_widgets/${id}/rotate_public_key`
      )
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, visitorWidgetKeys.lists())
      invalidateDetailQueries(queryClient, visitorWidgetKeys.detail(data.id))
    },
  })
}
